/*
 * Aura motion — ICFD rebuild, Aug 2026
 * Reveal-on-scroll with a per-row stagger. No dependencies.
 *
 * The `aura-motion` class is added to <html> by this script, and the CSS only
 * hides elements under that class. If this file fails to load, or the browser
 * lacks IntersectionObserver, or the visitor prefers reduced motion, nothing is
 * ever hidden.
 */
(function () {
  'use strict';

  /*
   * Hero fit.
   *
   * The intent router must clear the fold on desktop. That depends on the
   * chrome above the hero — announcement bar plus header — which is 66px in the
   * local build's placeholder nav and 119px with the real theme header. A
   * hardcoded subtraction is therefore wrong in one place or the other, so
   * measure it and let CSS clamp the result.
   */
  function fitHero() {
    var hero = document.querySelector('.aura-hero');
    var router = document.querySelector('.aura-router');
    if (!hero || !router) return;

    // NOT hero.offsetTop. The section wrapper is position:sticky, which makes it
    // the offsetParent, so offsetTop reads 0 and the hero sizes ~150px too tall.
    // #MainContent is static, so rect.top + scrollY is its constant document
    // offset — i.e. exactly the chrome above the hero — at any scroll position.
    var main = document.getElementById('MainContent') || hero.parentElement;
    var above = Math.round(main.getBoundingClientRect().top + window.pageYOffset);
    var rh = Math.round(router.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--aura-hero-fit', above + rh + 8 + 'px');

    // Publish the resolved hero height so the router can pin directly beneath it.
    // Read after the fit is applied, because the height is derived from it.
    var hh = Math.round(hero.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--aura-hero-h', hh + 'px');
  }

  if (document.readyState !== 'loading') fitHero();
  else document.addEventListener('DOMContentLoaded', fitHero);
  window.addEventListener('load', fitHero);
  window.addEventListener('resize', fitHero, { passive: true });
  document.addEventListener('shopify:section:load', fitHero);

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!('IntersectionObserver' in window) || reduced.matches) return;

  var root = document.documentElement;
  root.classList.add('aura-motion');

  var SEL = '[data-aura-reveal]';

  function reveal(el) {
    if (el.classList.contains('is-revealed')) return;
    el.classList.add('is-revealed');
    observer.unobserve(el);
    el.addEventListener(
      'transitionend',
      function () {
        el.classList.add('is-settled');
      },
      { once: true }
    );
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) reveal(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.1 }
  );

  /*
   * IntersectionObserver alone is not enough. Its callback only runs when an
   * element's intersection state changes, so anything the browser skips
   * entirely — scroll position restored on reload, an anchor jump, a flung
   * scroll — is never reported and would stay at opacity 0 permanently.
   * This sweep catches those: anything at or above the fold gets revealed
   * regardless of whether the observer ever saw it.
   */
  var ticking = false;

  function sweep() {
    ticking = false;
    parallax();
    var pending = document.querySelectorAll(SEL + ':not(.is-revealed)');

    if (!pending.length) {
      // Parallax still needs the scroll stream, so only detach when there is
      // nothing left for either system to do.
      if (!parallaxEls.length) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
      return;
    }

    var fold = window.innerHeight * 0.92;
    Array.prototype.forEach.call(pending, function (el) {
      if (el.getBoundingClientRect().top < fold) reveal(el);
    });
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(sweep);
    // requestAnimationFrame is PAUSED while the document is hidden — a page that
    // loads in a background tab would set this latch, never run sweep, and then
    // ignore every subsequent scroll for the life of the page. Release it on a
    // timer so the worst case is one extra sweep, not dead scroll handling.
    window.setTimeout(function () { ticking = false; }, 200);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  /*
   * Parallax. The image inside a [data-aura-parallax] container drifts against
   * the page as the container crosses the viewport. Travel is deliberately
   * small — depth, not spectacle — and every read happens in one pass before
   * any write, so a row of these cannot thrash layout.
   */
  var parallaxEls = [];
  // 'down' is a negative amplitude, not just a smaller one - it flips which
  // end of the scroll transit sits at the image's own top. With the default
  // positive 'band' amplitude, an element's static CSS position (its resting
  // crop) only appears at the transit's midpoint, and the image's true top
  // edge only comes into view at progress=1 (the section about to leave the
  // viewport). 'down' puts the image's true top at progress=0 instead - the
  // frame starts on the top of the source photo and drifts DOWN into it as
  // the section scrolls through, rather than drifting through a middle crop
  // in both directions. Added 11 Sep 2026 for the permission-band portrait,
  // which needs its subject (a necklace right at the top of the frame)
  // visible from the moment the section enters, not just at the tail end.
  var AMPLITUDE = { band: 0.25, soft: 0.05, down: -0.15 };

  function collectParallax(scope) {
    scope.querySelectorAll('[data-aura-parallax]').forEach(function (el) {
      var im = el.querySelector('img');
      if (im && parallaxEls.indexOf(el) === -1) parallaxEls.push(el);
    });
  }

  function parallax() {
    if (!parallaxEls.length) return;
    var vh = window.innerHeight;

    // read every frame first, then write — never interleave, or a grid of
    // tiles forces a layout recalculation per element
    var frames = parallaxEls.map(function (el) {
      var r = el.getBoundingClientRect();
      return {
        img: el.querySelector('img'),
        top: r.top,
        h: r.height,
        amp: AMPLITUDE[el.getAttribute('data-aura-parallax')] || AMPLITUDE.band
      };
    });

    frames.forEach(function (f) {
      if (!f.img || f.top > vh || f.top + f.h < 0) return;   // off screen
      var progress = (vh - f.top) / (vh + f.h);              // 0 entering, 1 leaving
      var shift = (progress - 0.5) * 2 * f.amp * f.h;
      f.img.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
    });
  }

  function register(scope) {
    // Stagger is scoped per container so each row counts from zero, rather
    // than accumulating delay down the whole page.
    scope.querySelectorAll('[data-aura-stagger]').forEach(function (group) {
      group.querySelectorAll(SEL).forEach(function (el, i) {
        el.style.setProperty('--aura-reveal-delay', i * 80 + 'ms');
      });
    });

    scope.querySelectorAll(SEL).forEach(function (el) {
      if (!el.classList.contains('is-revealed')) observer.observe(el);
    });

    collectParallax(scope);
    sweep();
  }

  register(document);

  // Scroll restoration lands after load on some browsers, so sweep again.
  window.addEventListener('load', sweep);

  // requestAnimationFrame is paused while a tab is in the background, so a page
  // that loads there can finish load with the sweep never having run. Sweep
  // once more when the tab becomes visible.
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) sweep();
  });

  // Theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    register(event.target);
  });

  // If the visitor turns reduced motion on mid-session, reveal everything.
  var onChange = function () {
    if (!reduced.matches) return;
    observer.disconnect();
    document.querySelectorAll(SEL).forEach(function (el) {
      el.classList.add('is-revealed', 'is-settled');
    });
  };

  if (reduced.addEventListener) reduced.addEventListener('change', onChange);
})();
